'use client'

import { trackMarketingFaqOpened } from '@/lib/analytics'
import { useState, useCallback } from 'react'
import { ArrowRight, ChevronDown } from '@/components/ui/icons'
import { Link } from '@/i18n/navigation'
import { SectionHeading } from '../components/SectionHeading'

export interface PricingFAQItem {
  id: string
  question: string
  answer: string
  /** An optional call to action under the answer (internal route). */
  link?: { label: string; href: string }
}

interface PricingFAQProps {
  faqs: PricingFAQItem[]
  /** Questions shown before "Show more"; omit to show them all. */
  initialCount?: number
}

// A hairline list, not a stack of boxes: the question is the row, the answer
// unfolds beneath it.
export function PricingFAQ({ faqs, initialCount }: PricingFAQProps) {
  const [openId, setOpenId] = useState<string | null>(null)
  const [showAll, setShowAll] = useState(false)
  const visible = initialCount && !showAll ? faqs.slice(0, initialCount) : faqs
  const hidden = faqs.length - visible.length

  const toggleFAQ = useCallback(
    (id: string) => {
      if (openId !== id) trackMarketingFaqOpened({ questionId: id })
      setOpenId(openId === id ? null : id)
    },
    [openId],
  )

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent, id: string) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault()
        toggleFAQ(id)
      }
    },
    [toggleFAQ],
  )

  return (
    <section aria-labelledby="pricing-faq-heading" className="grid gap-10 lg:grid-cols-[minmax(0,4fr)_minmax(0,8fr)]">
      <SectionHeading lead="Questions" className="lg:sticky lg:top-32 lg:self-start" />
      <div>
        <div className="border-t border-white/[0.08]" role="list">
          {visible.map(faq => {
            const isOpen = openId === faq.id
            return (
              <div key={faq.id} id={faq.id} className="border-b border-white/[0.08]" role="listitem">
                <button
                  onClick={() => toggleFAQ(faq.id)}
                  onKeyDown={e => handleKeyDown(e, faq.id)}
                  aria-expanded={isOpen}
                  aria-controls={`${faq.id}-content`}
                  className="flex w-full cursor-pointer items-center justify-between gap-4 py-5 text-start text-[16px] font-medium text-white transition-colors hover:text-white/80 md:text-[17px]"
                >
                  <span>{faq.question}</span>
                  <ChevronDown
                    className={`h-4 w-4 flex-shrink-0 text-white/40 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
                    aria-hidden="true"
                  />
                </button>
                <div
                  id={`${faq.id}-content`}
                  role="region"
                  aria-labelledby={faq.id}
                  className={`grid transition-all duration-200 ${isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}
                >
                  <div className="overflow-hidden">
                    <p
                      className={`max-w-[640px] text-[15px] leading-relaxed text-white/60 ${faq.link ? 'pb-3' : 'pb-5'}`}
                    >
                      {faq.answer}
                    </p>
                    {faq.link && (
                      <Link
                        href={faq.link.href}
                        className="group/link mb-5 inline-flex items-center gap-1.5 text-sm font-medium text-white/80 transition-colors hover:text-white"
                      >
                        {faq.link.label}
                        <ArrowRight
                          strokeWidth={1.5}
                          className="h-3.5 w-3.5 transition-transform duration-200 group-hover/link:translate-x-0.5"
                        />
                      </Link>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
        {hidden > 0 && (
          <button
            type="button"
            onClick={() => setShowAll(true)}
            className="mt-5 inline-flex items-center gap-1.5 text-sm text-white/50 transition-colors hover:text-white"
          >
            Show {hidden} more
            <ChevronDown className="h-3.5 w-3.5" aria-hidden="true" />
          </button>
        )}
      </div>
    </section>
  )
}
